import { useCallback, useContext, useEffect, useState } from "react";
import { TOContext } from "context/TContext";
import {
  useApplyCouponMutation,
  useBillingPaymentIntentUpdateMutation,
  useLazyGetOrderDetailQuery,
  useRemoveCouponMutation,
} from "api";
import { getForwardPaymentIntentId } from "helpers/getForwardPaymentIntentId";
import {
  getPromotionDetails,
  resolvePromotionDetailsFromOrder,
} from "helpers/promotionCharges";

const isCouponDetail = (item) =>
  Boolean(
    item?.couponId || item?.couponCode || item?.couponTitle || item?.couponName
  );

const mapPromotionToCoupon = (promotion) => ({
  ...promotion,
  uuid: promotion?.uuid || promotion?.promotionUuid,
  couponTitle: promotion?.displayLabel || promotion?.promotionName,
  couponName: promotion?.promotionName,
  couponCode: promotion?.promotionCode,
  isPromotion: true,
});

const couponCodeOf = (item) =>
  String(item?.promotionCode || item?.couponCode || item?.discountCode || "")
    .trim()
    .toUpperCase();

export const getRemovableCoupon = (coupon) => {
  if (!coupon) {
    return null;
  }

  const promotionCode = String(coupon.promotionCode || "").trim();
  if (coupon.isPromotion && !promotionCode) {
    return null;
  }
  const code = couponCodeOf(coupon);

  const uuid = coupon.uuid || coupon.promotionUuid;
  if (!uuid) {
    return null;
  }

  if (code || coupon.couponId) {
    return { ...coupon, uuid };
  }

  return null;
};

export const getAppliedCouponsFromCharges = (charges) => {
  const fromDiscountDetails = (
    Array.isArray(charges?.discountDetails) ? charges.discountDetails : []
  ).filter(isCouponDetail);

  const appliedCouponCodes = new Set(
    fromDiscountDetails.map(couponCodeOf).filter(Boolean)
  );

  // A promotion with no code is automatic. It stays in the Promotions total
  // and is not a coupon the customer can remove.
  const fromPromotions = getPromotionDetails(charges)
    .filter((item) => {
      const code = String(item?.promotionCode || "").trim().toUpperCase();
      return Boolean(code) && !appliedCouponCodes.has(code);
    })
    .map(mapPromotionToCoupon);

  return [...fromDiscountDetails, ...fromPromotions];
};

export const useCouponCode = (businessId, orderId) => {
  const [error, setError] = useState(null);
  const [loadingCoupon, setLoadingCoupon] = useState(false);
  const [couponResponse, setCouponResponse] = useState(null);
  const [loadingRemoveCoupon, setLoadingRemoveCoupon] = useState(false);

  const [applyCoupon] = useApplyCouponMutation();
  const [removeCoupon] = useRemoveCouponMutation();
  const [getOrderDetail] = useLazyGetOrderDetailQuery();
  const [updateBillingPaymentIntent] = useBillingPaymentIntentUpdateMutation();

  const {
    couponCode,
    setCouponCode,
    charges,
    setCharges,
    setActionInProgress,
  } = useContext(TOContext);

  const updateChargesSafely = useCallback(
    (options) => {
      const {
        discount = 0,
        chargesData,
        deliveryFee = 0,
        taxAmount,
        servicingFee = { ordersCoFee: 0, processingFee: 0, marketplaceFee: 0 },
      } = options;

      setCharges((prevCharges) => {
        const nextPromotionDetails = resolvePromotionDetailsFromOrder({
          charges: chargesData,
        });

        return {
          ...prevCharges,
          ...chargesData,
          discount: discount || 0,
          balanceDue: chargesData?.balanceDue,
          total: chargesData?.total,
          discountDetails: Array.isArray(chargesData?.discountDetails)
            ? chargesData.discountDetails
            : prevCharges?.discountDetails || [],
          promotionDiscount:
            chargesData?.promotionDiscount ?? prevCharges?.promotionDiscount,
          promotionDetails: Array.isArray(chargesData?.promotionDetails)
            ? nextPromotionDetails
            : prevCharges?.promotionDetails || [],
          deliveryFee: chargesData?.deliveryFee ?? (deliveryFee || 0),
          tipInfo: chargesData?.tipInfo
            ? {
                kitchen: Number(chargesData?.tipInfo?.kitchen) || 0,
                driver: Number(chargesData?.tipInfo?.driver) || 0,
              }
            : prevCharges?.tipInfo || { kitchen: 0, driver: 0 },
          taxAmount: chargesData?.taxAmount ?? (taxAmount || 0),
          servicingFee: chargesData?.servicingFee ?? servicingFee,
        };
      });
    },
    [setCharges],
  );

  const handleChangeCouponCode = (event) => {
    setCouponCode(event.target.value);
    setError("");
  };

  useEffect(() => {
    if (charges && charges?.discountDetails !== null) {
      const findCoupon = charges?.discountDetails?.find(
        (item) => item?.couponId,
      );
      if (findCoupon) {
        setCouponResponse(findCoupon);
      }
    }
  }, [charges]);

  const getOrderDetailsAfterTipApply = (orderId) => {
    const forwardPaymentIntentId = getForwardPaymentIntentId(orderId);
    if (forwardPaymentIntentId) {
      updateBillingPaymentIntent({
        oId: orderId,
        paymentIntentId: forwardPaymentIntentId,
      });
    }
    getOrderDetail({ oId: orderId })
      .then((response) => {
        const chargesData = response?.data?.data?.charges;
        const findCoupon = chargesData?.discountDetails?.find(
          (item) => item?.couponId,
        );
        const deliveryFee = chargesData?.deliveryFee || 0;
        const taxAmount = chargesData?.taxAmount || 0;
        const servicingFee = chargesData?.servicingFee || {};
        if (!response?.error) {
          updateChargesSafely({
            discount: findCoupon?.discountAmount,
            chargesData,
            deliveryFee,
            taxAmount,
            servicingFee,
          });
        }
      })
      .finally(() => {
        setActionInProgress(false);
      });
  };

  const handleSubmitCouponCode = () => {
    setLoadingCoupon(true);
    setActionInProgress(true);
    applyCoupon({ couponCode: couponCode, oid: orderId })
      .then((response) => {
        if (!response?.error) {
          getOrderDetailsAfterTipApply(orderId);
          if (
            response?.data?.charges?.discountDetails &&
            Array.isArray(response?.data?.charges?.discountDetails)
          ) {
            const findCoupon = response?.data?.charges?.discountDetails.find(
              (item) => item?.couponId,
            );
            if (findCoupon) {
              setCouponResponse(findCoupon);
            }
          }
          setCouponCode("");
        } else {
          setActionInProgress(false);
          setCouponResponse(null);
          setCouponCode("");
          if (Array.isArray(response?.error?.data?.message)) {
            setError(response?.error?.data?.message[0] || null);
          } else {
            setError(response?.error?.data?.message || null);
          }
        }
      })
      .finally(() => {
        setLoadingCoupon(false);
      });
  };

  const handleRemoveCoupon = (coupon) => {
    const target = getRemovableCoupon(
      coupon?.uuid || coupon?.couponId || coupon?.promotionCode
        ? coupon
        : couponResponse
    );
    if (!target?.uuid) {
      return;
    }
    setLoadingRemoveCoupon(true);
    setActionInProgress(true);
    removeCoupon({ oid: orderId, uuid: target.uuid })
      .then((response) => {
        if (!response?.error) {
          setCouponResponse(null);
          setCouponCode("");
          getOrderDetailsAfterTipApply(orderId);
        }
      })
      .finally(() => setLoadingRemoveCoupon(false));
  };

  return {
    couponCode,
    handleChangeCouponCode,
    handleSubmitCouponCode,
    loadingCoupon,
    couponResponse,
    appliedCoupons: getAppliedCouponsFromCharges(charges),
    handleRemoveCoupon,
    loadingRemoveCoupon,
    error,
  };
};
